import QuickInformationDisplay from "@components/QuickInformationDisplay/QuickInformationDisplay"
import JIDRInputCard from "@components/JIDRInputCard/JIDRInputCard"
import RedemptionTargetSelectPopUp from "@components/RedemptionTargetSelectPopUp/RedemptionTargetSelectPopUp"
import SectionLabel from "@components/SectionLabel/SectionLabel"
import TextInput from "@components/TextInput/TextInput"
import { MINIMUM_BANK_REDEMPTION } from "@constants/transaction"
import { useNumericKeyboard } from "@hooks/useNumericKeyboard"
import { toCurrencyFormat } from "@lib/utils"
import { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router"
import useFavoritePaymentBanks from "@hooks/useFavoritePaymentBanks"
import PrimaryButton from "@components/PrimaryButton/PrimaryButton"
import { useTranslation } from "react-i18next"
import {
    ScrollContainer,
    RedemptionDetailsContainer,
    BankInfoContainer,
    ButtonContainer,
    InfoContainer
} from "./RedeemBankContent.styles"
import { RedemptionOption } from "../../types/redemption"
import { SupportedBank } from "@hooks/useSupportedBank"
import BankAlertBox from "@components/BankAlertBox/BankAlertBox"

const SCROLL_OFFSET_MEMO = 200;
const FOCUS_SCROLL_DELAY = 500;

const RedeemBankAdditionalInformation = ({
    currentBalance,
    minimumRedemption,
    remainingLimit,
    totalDailyLimit,
    memo,
    onChangeMemo,
    onMemoFocus
}: {
    currentBalance: number | 'unavailable'
    minimumRedemption: number
    remainingLimit: number
    totalDailyLimit: number
    memo: string
    onChangeMemo: (value: React.ChangeEvent<HTMLInputElement>) => void
    onMemoFocus: () => void
}) => {
    const { t } = useTranslation()
    const memoInputRef = useRef<HTMLInputElement>(null);
    return (
        <div>
            <InfoContainer>
                <QuickInformationDisplay
                    fieldName={t('redeem.common.currentBalance')}
                    value={typeof currentBalance === 'number' ? `${toCurrencyFormat(currentBalance)} JIDR` : t('redeem.common.unavailable')}
                />
                <QuickInformationDisplay
                    fieldName={t('redeem.common.minimumRedemption')}
                    value={`${toCurrencyFormat(minimumRedemption)} JIDR`}
                />
                <QuickInformationDisplay
                    fieldName={t('redeem.bank.dailyLimit')}
                    value={`${toCurrencyFormat(remainingLimit)} JIDR / ${toCurrencyFormat(totalDailyLimit)} JIDR`}
                    boldValue
                />
            </InfoContainer>
            <TextInput
                value={memo}
                onChange={onChangeMemo}
                title={t('redeem.common.memo')}
                isOptional
                onFocus={onMemoFocus}
                placeholder={t('redeem.common.memoPlaceholder')}
                ref={memoInputRef}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === 'Tab') {
                        e.preventDefault();
                        memoInputRef.current?.blur();
                    }
                }}
            />
        </div>
    )
}

const RedeemBankContent = ({
    currentBalance,
    remainingLimit,
    totalDailyLimit,
    preselectedBank,
    prefilledAmount,
    redemptionOptions,
    supportedBanks,
    splitDisbursementCountLimit,
    onRemoveBank,
}: {
    currentBalance: number | 'unavailable'
    remainingLimit: number
    totalDailyLimit: number
    preselectedBank?: RedemptionOption
    prefilledAmount?: number
    redemptionOptions: RedemptionOption[]
    supportedBanks?: SupportedBank[]
    splitDisbursementCountLimit: number
    onRemoveBank?: (bank_uuid: string) => Promise<void>
}) => {
    const navigate = useNavigate()
    const { isFavorite, toggleFavorite } = useFavoritePaymentBanks('REDEMPTION')
    const { t } = useTranslation()
    const containerRef = useRef<HTMLDivElement>(null);
    const [bankMemo, setBankMemo] = useState('')
    const [selectedBank, setSelectedBank] = useState<RedemptionOption | undefined>(preselectedBank)
    const { amount, handleKeyboardPress, setAmount } = useNumericKeyboard(prefilledAmount || 0)
    const [amountError, setAmountError] = useState({
        isError: false,
        message: ''
    })

    const validateAmount = useCallback((amountToValidate?: number) => {
        const valueToValidate = amountToValidate ?? amount
        if (valueToValidate < MINIMUM_BANK_REDEMPTION) {
            setAmountError({
                isError: true,
                message: t('redeem.common.errorMessages.minimumAmount', { amount: toCurrencyFormat(MINIMUM_BANK_REDEMPTION) })
            })
        } else if (typeof currentBalance !== 'number' || valueToValidate > currentBalance) {
            setAmountError({
                isError: true,
                message: t('redeem.common.errorMessages.insufficientBalance')
            })
        } else if (valueToValidate > remainingLimit) {
            setAmountError({
                isError: true,
                message: t('redeem.common.errorMessages.maximumAmount', { amount: toCurrencyFormat(remainingLimit) })
            })
        } else {
            setAmountError({
                isError: false,
                message: ''
            })
        }
    }, [amount, currentBalance, remainingLimit, t])

    useEffect(() => {
        if (prefilledAmount && prefilledAmount > 0) {
            validateAmount()
        }
    }, [prefilledAmount, validateAmount])

    const handleFocus = () => {
        setTimeout(() => {
            if (containerRef.current) {
                const startPosition = containerRef.current.scrollTop;
                const targetPosition = SCROLL_OFFSET_MEMO;
                const duration = 100; // milliseconds - adjust for faster/slower animation
                const startTime = performance.now();

                const animateScroll = (currentTime: number) => {
                    const elapsedTime = currentTime - startTime;
                    const progress = Math.min(elapsedTime / duration, 1);
                    // Use easeOutQuad easing function for a natural feel
                    const easeProgress = 1 - (1 - progress) * (1 - progress);

                    containerRef.current!.scrollTop = startPosition + (targetPosition - startPosition) * easeProgress;

                    if (progress < 1) {
                        requestAnimationFrame(animateScroll);
                    }
                };

                requestAnimationFrame(animateScroll);
            }
        }, FOCUS_SCROLL_DELAY);
    }

    return (
        <ScrollContainer ref={containerRef}>
            <RedemptionDetailsContainer>
                <SectionLabel>{t('redeem.common.redemptionDetails')}</SectionLabel>
                <JIDRInputCard
                    handleKeyboardPress={handleKeyboardPress}
                    title={t('redeem.common.inputAmount')}
                    amount={amount}
                    errorText={amountError.message}
                    isAmountError={amountError.isError}
                    onBlur={() => validateAmount()}
                    onMax={typeof currentBalance === 'number' ? () => {
                        const maxAmount = Math.min(currentBalance, remainingLimit, totalDailyLimit)
                        setAmount(maxAmount)
                        setTimeout(() => validateAmount(maxAmount), 0)
                    } : undefined}
                    children={
                        <RedeemBankAdditionalInformation
                            currentBalance={currentBalance}
                            minimumRedemption={MINIMUM_BANK_REDEMPTION}
                            remainingLimit={remainingLimit}
                            totalDailyLimit={totalDailyLimit}
                            memo={bankMemo}
                            onChangeMemo={(e) => setBankMemo(e.target.value)}
                            onMemoFocus={handleFocus}
                        />
                    }
                />
            </RedemptionDetailsContainer>
            <BankInfoContainer>
                <SectionLabel>{t('redeem.bank.bankInformation')}</SectionLabel>
                <RedemptionTargetSelectPopUp
                    type="bank"
                    options={[...redemptionOptions].sort(
                        (a, b) => (isFavorite(b.id) ? 1 : 0) - (isFavorite(a.id) ? 1 : 0)
                    )}
                    preselect={selectedBank}
                    onSelect={setSelectedBank}
                    currentAmount={amount}
                    onRemove={onRemoveBank}
                    isFavorite={isFavorite}
                    onFavoriteToggle={toggleFavorite}
                />
                <BankAlertBox
                    cutOffTime={selectedBank && supportedBanks?.find(bank => bank.code === selectedBank.provider)?.cut_off_time}
                    inputtedAmount={amount}
                    splitDisbursementCountLimit={splitDisbursementCountLimit}
                    maximumAmountPerDisbursement={selectedBank && supportedBanks?.find(bank => bank.display_name === selectedBank.provider)?.max_amount}
                />
            </BankInfoContainer>
            <ButtonContainer>
                <PrimaryButton
                    onClick={() => navigate('/redeem-confirmation/bank', {
                        state: {
                            redeemInformation: JSON.stringify({
                                redemptionTarget: selectedBank,
                                amount,
                                memo: bankMemo,
                            })
                        }
                    }
                    )}
                    disabled={
                        !selectedBank ||
                        amount <= 0 ||
                        amountError.isError
                    }
                >
                    {t('redeem.common.continueButton')}
                </PrimaryButton>
            </ButtonContainer>
        </ScrollContainer>
    )
}

export default RedeemBankContent
